<template>
  <div class="academic-records-list p-4">
    <RecordListPrintHeader title="テスト成績管理" />

    <div
      v-for="member in members"
      :key="member.id"
      :data-member-id="member.id"
      class="academic-records-list__user-row border mb-4">
      <div class="d-flex">
        <!-- ユーザー情報（左パネル） -->
        <div class="academic-records-list__user-info">
          <div
            class="academic-records-list__user-info--clickable"
            @click="goToProfile(member.id)">
            <RecordListMemberInfo
              :member="member"
              :highlighted="member.id === highlightMemberId"
              show-sort-no />
          </div>
          <button
            v-if="totalSemesterCount(member) > 3"
            type="button"
            class="academic-records-list__expand-btn d-none d-md-block"
            @click="toggleExpand(member.id)">
            {{ isExpanded(member.id) ? '▲ 折りたたむ' : '▼ すべて表示' }}
          </button>
        </div>

        <!-- 成績データ（右パネル） -->
        <div class="academic-records-list__records-area">
          <div
            v-if="!member.academicGrades.length"
            class="fs-s text--grey05 p-3">
            成績データなし
          </div>
          <div
            v-for="academicRecord in visibleGrades(member)"
            :key="academicRecord.id"
            class="mb-3">
            <div class="academic-records-list__grade-title fs-m mb-2">
              {{ academicRecord.gradeCode | gradeCode }}
            </div>
            <div
              v-for="(semester, semesterIndex) in academicRecord.semesters"
              :key="semesterIndex"
              class="academic-records-list__semester-row">
              <!-- 通知表ブロック -->
              <div class="academic-records-list__block">
                <div class="academic-records-list__block-header">
                  <span class="academic-records-list__badge">{{ semester.semester | semester }}</span>
                  <span class="fs-s font-weight-bold academic-records-list__block-title">通知表</span>
                </div>

                <!-- 科目成績テーブル -->
                <template v-if="hasSubjectScores(semester)">
                  <div
                    v-for="(chunk, chunkIdx) in chunkSubjectScores(semester.subjectScores)"
                    :key="chunkIdx">
                    <table class="academic-records-list__score-table">
                      <thead>
                        <tr>
                          <th
                            v-for="(score, idx) in chunk"
                            :key="idx">
                            {{ score.subjectName | subjectName }}
                          </th>
                          <th
                            v-for="i in (5 - chunk.length)"
                            :key="`eh-${chunkIdx}-${i}`" />
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td
                            v-for="(score, idx) in chunk"
                            :key="idx">
                            {{ orDash(score.score) }}
                          </td>
                          <td
                            v-for="i in (5 - chunk.length)"
                            :key="`ed-${chunkIdx}-${i}`"
                            class="academic-records-list__score-table-empty" />
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- 成績集計 -->
                  <table
                    v-if="isJuniorHighSchool(academicRecord.gradeCode)"
                    class="academic-records-list__score-table academic-records-list__score-table--summary">
                    <thead>
                      <tr>
                        <th>5教科成績計</th>
                        <th>9教科成績計</th>
                        <th>成績平均</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{{ getSumOf5SubjectScores(semester) }}</td>
                        <td>{{ getSumOfSubjectScores(semester) }}</td>
                        <td>{{ getAverageOfSubjectScores(semester) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <table
                    v-else
                    class="academic-records-list__score-table academic-records-list__score-table--summary">
                    <thead>
                      <tr>
                        <th>成績計</th>
                        <th>成績平均</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{{ getSumOfSubjectScores(semester) }}</td>
                        <td>{{ getAverageOfSubjectScores(semester) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </template>

                <!-- 出欠情報 -->
                <table class="academic-records-list__score-table academic-records-list__score-table--attendance">
                  <thead>
                    <tr>
                      <th>出席日数</th>
                      <th>欠席日数</th>
                      <th>遅刻日数</th>
                      <th>早退日数</th>
                      <th>忌引日数</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ orDash(semester.attendanceDays) }}</td>
                      <td>{{ orDash(semester.absenceDays) }}</td>
                      <td>{{ orDash(semester.lateDays) }}</td>
                      <td>{{ orDash(semester.earlyLeaveDays) }}</td>
                      <td>{{ orDash(semester.exclusionDays) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- テスト結果ブロック -->
              <template v-if="semester.testResults && semester.testResults.length">
                <div
                  v-for="(testResult, testResultIndex) in semester.testResults"
                  :key="testResultIndex"
                  class="academic-records-list__block">
                  <div class="academic-records-list__block-header">
                    <span class="academic-records-list__badge">{{ semester.semester | semester }}</span>
                    <span class="fs-s font-weight-bold">テスト結果</span>
                  </div>

                  <!-- テスト名・日付 -->
                  <div class="d-flex align-items-center py-1 mb-2">
                    <span class="academic-records-list__badge">{{ testResult.testName | testName }}</span>
                    <span class="fs-xs">{{ testResult.testDate | prettyDate }}</span>
                  </div>

                  <!-- テスト集計 -->
                  <table
                    v-if="isJuniorHighSchool(academicRecord.gradeCode)"
                    class="academic-records-list__score-table academic-records-list__score-table--summary">
                    <thead>
                      <tr>
                        <th>5教科合計点</th>
                        <th>9教科合計点</th>
                        <th>テスト平均点</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{{ getSumOfTestResults5SubjectScores(testResult) }}</td>
                        <td>{{ getSumOfTestResultsSubjectScores(testResult) }}</td>
                        <td>{{ getAverageOfTestResultsSubjectScores(testResult) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <table
                    v-else
                    class="academic-records-list__score-table academic-records-list__score-table--summary">
                    <thead>
                      <tr>
                        <th>テスト合計点</th>
                        <th>テスト平均点</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{{ getSumOfTestResultsSubjectScores(testResult) }}</td>
                        <td>{{ getAverageOfTestResultsSubjectScores(testResult) }}</td>
                      </tr>
                    </tbody>
                  </table>

                  <!-- テスト科目点数 -->
                  <div
                    v-for="(chunk, chunkIdx) in chunkSubjectScores(testResult.subjectScores)"
                    :key="chunkIdx">
                    <table class="academic-records-list__score-table academic-records-list__score-table--attendance">
                      <thead>
                        <tr>
                          <th
                            v-for="(score, idx) in chunk"
                            :key="idx">
                            {{ score.subjectName | subjectName }}
                          </th>
                          <th
                            v-for="i in (5 - chunk.length)"
                            :key="`teh-${chunkIdx}-${i}`" />
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td
                            v-for="(score, idx) in chunk"
                            :key="idx">
                            {{ displayTestScore(score.score) }}
                          </td>
                          <td
                            v-for="i in (5 - chunk.length)"
                            :key="`ted-${chunkIdx}-${i}`" />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <button
        v-if="totalSemesterCount(member) > 3"
        type="button"
        class="academic-records-list__expand-btn academic-records-list__expand-btn--mobile d-md-none"
        @click="toggleExpand(member.id)">
        {{ isExpanded(member.id) ? '▲ 折りたたむ' : '▼ すべて表示' }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import RecordListPrintHeader from '@/components/view/records/RecordListPrintHeader.vue'
import RecordListMemberInfo from '@/components/view/records/RecordListMemberInfo.vue'
import {
  isJuniorHighSchool,
  juniorHighSchool5SubjectTitles,
  academicRecordGradeCodes,
} from '@/helper/profile'
import { buildMemberFilterApiParams, buildOrderingByFilterQuery, resolveFilterQuery } from '@/helper/records'

export default {
  components: {
    RecordListPrintHeader,
    RecordListMemberInfo,
  },
  data() {
    return {
      members: [],
      expandedMemberIds: {},
      highlightMemberId: null,
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('recordProfile', ['filterParams']),
  },
  async created() {
    this.$store.commit('startLoading')
    try {
      await this.fetchData()
    } finally {
      this.$store.commit('finishLoading')
    }
    this.$nextTick(() => {
      const memberId = this.$store.state.recordProfile.returnMemberId
      if (memberId) {
        this.$store.commit('recordProfile/clearReturnMemberId')
        this.highlightMemberId = memberId
        const el = document.querySelector(`[data-member-id="${memberId}"]`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    })
  },
  methods: {
    async fetchData() {
      const q = resolveFilterQuery(this.$route.query, this.filterParams)
      const memberParams = buildMemberFilterApiParams(q)
      const recordParams = {
        year_grade__in: q.academic_grade || academicRecordGradeCodes.join(','),
        ...memberParams,
      }
      const [recordRes, usersRes] = await Promise.all([
        this.$axios.get('/admin/profiles/academic-grades/', { params: recordParams }),
        this.$axios.get('/users/', { params: { ...memberParams, ordering: buildOrderingByFilterQuery(q), limit: 999999 } }),
      ])
      const users = usersRes.data.results || []
      const recordMap = (recordRes.data || []).reduce((acc, item) => {
        acc[item.id] = item.academicGrades
        return acc
      }, {})
      this.members = users.map(user => ({
        ...user,
        academicGrades: recordMap[user.id] || [],
      }))
    },
    chunkSubjectScores(subjectScores, size = 5) {
      const result = []
      for (let i = 0; i < subjectScores.length; i += size) {
        result.push(subjectScores.slice(i, i + size))
      }
      return result
    },
    hasSubjectScores(semester) {
      return semester.subjectScores && semester.subjectScores.length > 0
    },
    isJuniorHighSchool(gradeCode) {
      return isJuniorHighSchool(gradeCode)
    },
    getValidSubjectScores(subjectScores) {
      return subjectScores.filter(s => s.score != null)
    },
    getSumOfSubjectScores(semester) {
      return this.getValidSubjectScores(semester.subjectScores).reduce((sum, s) => sum + Number(s.score), 0)
    },
    getAverageOfSubjectScores(semester) {
      const valid = this.getValidSubjectScores(semester.subjectScores)
      if (valid.length === 0) return 0
      return (this.getSumOfSubjectScores(semester) / valid.length).toFixed(1)
    },
    getSumOf5SubjectScores(semester) {
      return this.getValidSubjectScores(semester.subjectScores)
        .filter(s => juniorHighSchool5SubjectTitles.includes(s.subjectName))
        .reduce((sum, s) => sum + Number(s.score), 0)
    },
    getSumOfTestResultsSubjectScores(testResult) {
      return this.getValidSubjectScores(testResult.subjectScores).reduce((sum, s) => sum + Number(s.score), 0)
    },
    getSumOfTestResults5SubjectScores(testResult) {
      return this.getValidSubjectScores(testResult.subjectScores)
        .filter(s => juniorHighSchool5SubjectTitles.includes(s.subjectName))
        .reduce((sum, s) => sum + Number(s.score), 0)
    },
    getAverageOfTestResultsSubjectScores(testResult) {
      const valid = this.getValidSubjectScores(testResult.subjectScores)
      if (valid.length === 0) return 0
      return (this.getSumOfTestResultsSubjectScores(testResult) / valid.length).toFixed(1)
    },
    orDash(v) {
      return v != null ? v : '-'
    },
    displayTestScore(v) {
      if (v == null) return '-'
      return this.$options.filters.trimDecimalZero(v)
    },
    goToProfile(memberId) {
      this.$router.push({
        path: `/members/${memberId}/profile`,
        query: { backTo: 'recordList' },
      })
    },
    totalSemesterCount(member) {
      return member.academicGrades.reduce((sum, grade) => sum + (grade.semesters || []).length, 0)
    },
    visibleGrades(member) {
      if (this.isExpanded(member.id)) return member.academicGrades
      let remaining = 3
      return member.academicGrades.reduce((result, grade) => {
        if (remaining <= 0) return result
        const semesters = (grade.semesters || []).slice(0, remaining)
        remaining -= semesters.length
        if (semesters.length > 0) result.push({ ...grade, semesters })
        return result
      }, [])
    },
    isExpanded(memberId) {
      return !!this.expandedMemberIds[memberId]
    },
    toggleExpand(memberId) {
      this.$set(this.expandedMemberIds, memberId, !this.expandedMemberIds[memberId])
    },
  },
}
</script>

<style lang="scss">
@page {
  size: A4 landscape;
}
</style>

<style lang="scss" scoped>
.academic-records-list {
  background-color: white;
  min-height: 100vh;

  &__user-row {
    page-break-inside: avoid;
  }

  &__user-info {
    width: 180px;
    flex-shrink: 0;
    border-right: 1px solid $grey11;
    display: flex;
    flex-direction: column;

    &--clickable {
      padding: 12px;
      cursor: pointer;

      &:hover {
        background-color: $grey03;
      }
    }
  }

  &__expand-btn {
    margin-top: auto;
    padding: 8px 12px;
    font-size: $fs-s;
    color: $l-blue;
    background: none;
    border: none;
    cursor: pointer;
    text-align: right;
    align-self: flex-end;

    &:hover {
      text-decoration: underline;
    }

    &--mobile {
      width: 100%;
      text-align: center;
      border-top: 1px solid $grey11;
      padding: 10px 12px;
    }
  }

  &__records-area {
    flex: 1;
    padding: 12px;
    overflow-x: auto;
  }

  &__grade-title {
    border-bottom: 2px solid $l-blue;
    padding-bottom: 4px;
    margin-bottom: 8px;
  }

  &__semester-row {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;
    align-items: stretch;
  }

  &__block {
    flex: 0 0 auto;
    width: 460px;
    border: 1px solid $grey11;
    padding: 12px;
  }

  &__block-header {
    display: flex;
    align-items: center;
    padding: 3px 0;
    margin-bottom: 4px;
  }

  &__block-title {
    border-bottom: 2px solid $l-blue;
    padding-bottom: 2px;
  }

  &__badge {
    background-color: $grey03;
    padding: 4px 10px;
    font-size: $fs-s;
    border-radius: 2px;
    white-space: nowrap;
    margin-right: 6px;
  }

  &__score-table {
    width: 100%;
    table-layout: fixed;
    margin-bottom: 4px;
    border-collapse: separate;
    border-spacing: 4px 2px;

    th, td {
      text-align: center;
      font-size: $fs-s;
      line-height: 1.3;
      white-space: nowrap;
      font-weight: normal;
    }

    th {
      background-color: transparent;
      padding: 2px 4px;
    }

    td {
      background-color: $grey03;
      padding: 8px 4px;
    }

    &-empty {
      background-color: transparent !important;
    }

    &--attendance {
      border-collapse: collapse;
      border-spacing: 0;
      margin-top: 8px;

      th, td {
        border: 1px solid $grey11;
        padding: 4px 8px;
        background-color: transparent;
      }
    }
  }

  @media print {
    &__records-area {
      overflow-x: visible;
      min-width: 0;
    }

    &__semester-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 12px;
      min-width: 0;
    }

    &__block {
      width: auto;
      flex: none;
      min-width: 0;
    }

    &__score-table {
      th, td {
        white-space: normal;
        overflow-wrap: break-word;
      }
    }
  }
}

.fs-xs {
  font-size: 10px;
}

@media (max-width: 767px) {
  .academic-records-list {
    &__user-row > .d-flex {
      flex-direction: column;
    }

    &__user-info {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid $grey11;
    }

    &__score-table {
      th, td {
        font-size: 12px;
        padding: 3px 4px;
      }
    }
  }
}
</style>
